import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, ChevronRight, Clock, CreditCard, Trash2, CheckSquare, Package, MessageSquare, Settings, AlarmClock } from "lucide-react";
import {
  Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import PageHeader from "@/components/shared/PageHeader";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { buildDeepLink } from "@/lib/deepLinks";

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  order_id: string | null;
  is_read: boolean;
  created_at: string;
  category?: string;
  action_url?: string;
  action_data?: any;
}

interface NotificationsSheetProps {
  children: React.ReactNode;
}

const CATEGORY_CONFIG: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  orders: { label: "Orders", icon: <Package className="h-3.5 w-3.5" />, color: "text-blue-500" },
  tasks: { label: "Tasks", icon: <CheckSquare className="h-3.5 w-3.5" />, color: "text-green-500" },
  messages: { label: "Messages", icon: <MessageSquare className="h-3.5 w-3.5" />, color: "text-purple-500" },
  system: { label: "System", icon: <Settings className="h-3.5 w-3.5" />, color: "text-muted-foreground" },
};

const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  all: "All notifications across messages, orders, tasks, channels, and system events.",
  messages: "Direct message alerts, mentions, and chat activity.",
  tasks: "Todo reminders, follow-ups, and task updates.",
  orders: "Order lifecycle and payment-related updates.",
  channel_post: "New posts published in channels you follow.",
  system: "General app notices and account-level information.",
};

function categorizeNotification(n: Notification): string {
  if (n.category && n.category !== "system") return n.category;
  if (n.order_id || n.type?.includes("order") || n.type?.includes("payment")) return "orders";
  if (n.type?.includes("task") || n.type?.includes("todo") || n.type?.includes("reminder")) return "tasks";
  if (n.type?.includes("message") || n.type?.includes("mention")) return "messages";
  return "system";
}

export default function NotificationsSheet({ children }: NotificationsSheetProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [depositOrders, setDepositOrders] = useState<any[]>([]);
  const suppressReloadRef = useRef(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const emitNotificationsChanged = () => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("notifications:changed"));
    }
  };

  const loadData = async () => {
    if (!user) return;
    const [{ data: notifs }, { data: orders }] = await Promise.all([
      supabase
        .from("notifications")
        .select("id, type, title, message, order_id, is_read, created_at, category, action_url, action_data")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(50),
      supabase
        .from("business_orders")
        .select("id, total_price, amount_paid, listing_id, seller_id, conversation_id, created_at, updated_at")
        .eq("buyer_id", user.id)
        .eq("status", "deposit_paid"),
    ]);
    setNotifications((notifs as Notification[]) ?? []);
    setDepositOrders((orders ?? []).filter((o: any) => o.total_price - o.amount_paid > 0));
  };

  useEffect(() => {
    if (open && user) loadData();
    if (!open) {
      setSelectionMode(false);
      setSelectedIds(new Set());
      setActiveCategory("all");
    }
  }, [open, user]);

  useEffect(() => {
    if (!user) return;
    const reloadIfNotSuppressed = () => {
      if (!suppressReloadRef.current) loadData();
    };
    const channel = supabase
      .channel("notifications-rt")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` }, reloadIfNotSuppressed)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` }, reloadIfNotSuppressed)
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` }, reloadIfNotSuppressed)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user]);

  const handleGoToOrder = (conversationId: string | null) => {
    setOpen(false);
    navigate(conversationId ? `/chat/${conversationId}` : "/orders");
  };

  const markAsRead = async (id: string) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    const { error } = await supabase.from("notifications").update({ is_read: true }).eq("id", id);
    if (error) loadData();
    else emitNotificationsChanged();
  };

  const getNotificationDestination = (notification: Notification): string | null => {
    const normalizeActionUrl = (raw: string | null | undefined): string | null => {
      if (!raw) return null;
      try {
        const parsed = new URL(raw, window.location.origin);
        return `${parsed.pathname}${parsed.search}${parsed.hash}`;
      } catch {
        return raw.startsWith("/") ? raw : null;
      }
    };

    const actionData = (notification.action_data ?? {}) as Record<string, any>;
    const conversationId =
      actionData.conversation_id ??
      actionData.conversationId ??
      actionData.chat_id ??
      actionData.chatId ??
      null;
    const messageId =
      actionData.message_id ??
      actionData.messageId ??
      actionData.msg_id ??
      actionData.msgId ??
      actionData.source_message_id ??
      actionData.sourceMessageId ??
      null;
    const todoId = actionData.todo_id ?? actionData.todoId ?? null;

    if (conversationId && messageId) {
      return buildDeepLink("chat_message", { conversation_id: conversationId, message_id: messageId });
    }
    if (conversationId) {
      return `/chat/${conversationId}`;
    }
    if (todoId) {
      return buildDeepLink("todo", { todo_id: todoId });
    }
    if (notification.order_id) {
      return "/orders";
    }

    const actionUrl = normalizeActionUrl(notification.action_url);
    if (actionUrl) return actionUrl;

    return null;
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (selectionMode) {
      toggleSelect(notification.id);
      return;
    }
    if (!notification.is_read) await markAsRead(notification.id);
    const destination = getNotificationDestination(notification);
    if (destination) {
      setOpen(false);
      navigate(destination);
    } else if (notification.order_id) {
      handleGoToOrder(null);
    }
  };

  const handleSnooze = async (notification: Notification) => {
    setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
    await supabase.from("notifications").update({ is_read: false }).eq("id", notification.id);
    toast.success("Snoozed for later");
  };

  const deleteNotification = async (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    suppressReloadRef.current = true;
    const { error } = await supabase.from("notifications").delete().eq("id", id);
    setTimeout(() => { suppressReloadRef.current = false; }, 2000);
    if (error) { toast.error("Failed to delete notification"); loadData(); }
    else emitNotificationsChanged();
  };

  const clearAllRead = async () => {
    const readIds = notifications.filter((n) => n.is_read).map((n) => n.id);
    if (readIds.length === 0) return;
    const confirmed = window.confirm(`Delete ${readIds.length} read notification${readIds.length > 1 ? "s" : ""}?`);
    if (!confirmed) return;
    setNotifications((prev) => prev.filter((n) => !n.is_read));
    suppressReloadRef.current = true;
    const { error } = await supabase.from("notifications").delete().in("id", readIds);
    setTimeout(() => { suppressReloadRef.current = false; }, 2000);
    if (error) { toast.error("Failed to clear notifications"); loadData(); }
    else emitNotificationsChanged();
  };

  const deleteAllNotifications = async () => {
    if (notifications.length === 0) return;
    const confirmed = window.confirm(`Delete all ${notifications.length} notifications? This cannot be undone.`);
    if (!confirmed) return;
    const allIds = notifications.map((n) => n.id);
    setNotifications([]);
    setSelectedIds(new Set());
    setSelectionMode(false);
    suppressReloadRef.current = true;
    const { error } = await supabase.from("notifications").delete().in("id", allIds);
    setTimeout(() => { suppressReloadRef.current = false; }, 2000);
    if (error) { toast.error("Failed to delete all notifications"); loadData(); }
    else { toast.success("All notifications deleted"); emitNotificationsChanged(); }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredNotifications.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredNotifications.map((n) => n.id)));
    }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const confirmed = window.confirm(`Delete ${ids.length} selected notification${ids.length > 1 ? "s" : ""}?`);
    if (!confirmed) return;
    setNotifications((prev) => prev.filter((n) => !selectedIds.has(n.id)));
    setSelectedIds(new Set());
    setSelectionMode(false);
    suppressReloadRef.current = true;
    const { error } = await supabase.from("notifications").delete().in("id", ids);
    setTimeout(() => { suppressReloadRef.current = false; }, 2000);
    if (error) { toast.error("Failed to delete notifications"); loadData(); }
    else { toast.success(`Deleted ${ids.length} notification${ids.length > 1 ? "s" : ""}`); emitNotificationsChanged(); }
  };

  // Group notifications by category
  const filteredNotifications = activeCategory === "all"
    ? notifications
    : notifications.filter((n) => categorizeNotification(n) === activeCategory);

  const grouped = filteredNotifications.reduce<Record<string, Notification[]>>((acc, n) => {
    const cat = categorizeNotification(n);
    (acc[cat] = acc[cat] ?? []).push(n);
    return acc;
  }, {});

  const readNotifications = filteredNotifications.filter((n) => n.is_read);
  const totalOutstanding = depositOrders.reduce((sum, order) => sum + (order.total_price - order.amount_paid), 0);
  const categoryOrder = ["tasks", "orders", "messages", "system"];
  const extraCategories = Object.keys(grouped).filter((cat) => !categoryOrder.includes(cat));
  const visibleCategoryOrder = [...categoryOrder, ...extraCategories];
  const allCategories = notifications.reduce<Record<string, number>>((acc, n) => {
    const cat = categorizeNotification(n);
    acc[cat] = (acc[cat] ?? 0) + 1;
    return acc;
  }, {});
  const activeCategoryDescription = CATEGORY_DESCRIPTIONS[activeCategory]
    ?? `${activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)} alerts.`;

  const headerRightAction = selectionMode ? (
    <Button
      variant="ghost"
      size="sm"
      className="rounded-xl text-xs"
      onClick={() => { setSelectionMode(false); setSelectedIds(new Set()); }}
    >
      Cancel
    </Button>
  ) : (
    <Button
      variant="ghost"
      size="sm"
      className="rounded-xl text-xs"
      onClick={() => setSelectionMode(true)}
      disabled={notifications.length === 0}
    >
      Select
    </Button>
  );

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{children}</SheetTrigger>
      <SheetContent side="right" mobileBackHeader className="w-full p-0 sm:max-w-md flex flex-col">
        <SheetTitle className="sr-only">Notifications</SheetTitle>
        <SheetDescription className="sr-only">
          View alerts, mark notifications as read, and manage them.
        </SheetDescription>

        <PageHeader
          title="Notifications"
          onBack={() => setOpen(false)}
          rightAction={headerRightAction}
        />

        <div className="px-4 pb-2">
          <ScrollArea className="w-full whitespace-nowrap">
            <div className="flex items-center gap-2">
              <Button
                variant={activeCategory === "all" ? "default" : "outline"}
                size="sm"
                className="rounded-full h-7 text-xs px-3"
                onClick={() => setActiveCategory("all")}
              >
                All ({notifications.length})
              </Button>
              {Object.entries(allCategories).map(([cat, count]) => {
                const cfg = CATEGORY_CONFIG[cat] ?? {
                  ...CATEGORY_CONFIG.system,
                  label: cat.charAt(0).toUpperCase() + cat.slice(1),
                };
                return (
                  <Button
                    key={cat}
                    variant={activeCategory === cat ? "default" : "outline"}
                    size="sm"
                    className="rounded-full h-7 text-xs px-3"
                    onClick={() => setActiveCategory(cat)}
                  >
                    {cfg.label} ({count})
                  </Button>
                );
              })}
            </div>
          </ScrollArea>
          <p className="mt-2 text-[11px] text-muted-foreground">
            {activeCategoryDescription}
          </p>
        </div>

        {!selectionMode && notifications.length > 0 && (
          <div className="px-4 pb-1 flex items-center justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={clearAllRead} className="flex text-xs text-destructive hover:text-destructive" disabled={readNotifications.length === 0}>
              <Trash2 className="mr-1 h-3.5 w-3.5" /> Clear read
            </Button>
            <Button variant="ghost" size="sm" onClick={deleteAllNotifications} className="flex text-xs text-destructive hover:text-destructive">
              <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete all
            </Button>
          </div>
        )}

        <ScrollArea className="flex-1">
          {depositOrders.length > 0 && (
            <div className="mx-4 mb-4 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
              <div className="mb-1 flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-amber-600" />
                <span className="text-sm font-semibold text-foreground">Outstanding Balances</span>
              </div>
              <p className="mb-3 text-xs text-muted-foreground">
                {depositOrders.length} order{depositOrders.length > 1 ? "s" : ""} with remaining balances totalling{" "}
                <span className="font-bold text-foreground">{totalOutstanding} credits</span>.
              </p>
              <div className="space-y-2">
                {depositOrders.map((order) => (
                  <button
                    key={order.id}
                    onClick={() => handleGoToOrder(order.conversation_id)}
                    className="flex w-full items-center justify-between rounded-lg border border-border bg-card p-3 text-left transition-colors hover:bg-secondary/50"
                  >
                    <div>
                      <p className="text-sm font-medium text-foreground">{order.total_price - order.amount_paid} credits remaining</p>
                      <p className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        Deposit paid {formatDistanceToNow(new Date(order.updated_at), { addSuffix: true })}
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {notifications.length > 0 ? (
            <div className="space-y-1 px-4 pb-24">
              {visibleCategoryOrder.map((cat) => {
                const items = grouped[cat];
                if (!items || items.length === 0) return null;
                const cfg = CATEGORY_CONFIG[cat] ?? {
                  ...CATEGORY_CONFIG.system,
                  label: cat.charAt(0).toUpperCase() + cat.slice(1),
                };
                return (
                  <div key={cat} className="mb-3">
                    <div className={cn("flex items-center gap-1.5 text-xs font-medium mb-1.5 px-1", cfg.color)}>
                      {cfg.icon}
                      {cfg.label} ({items.length})
                    </div>
                    <div className="space-y-2">
                      {items.map((notification) => (
                        <div
                          key={notification.id}
                          className={cn(
                            "flex items-start gap-2 rounded-xl border p-3 transition-colors",
                            !notification.is_read ? "border-primary/30 bg-primary/5" : "border-border",
                            selectionMode && selectedIds.has(notification.id) && "ring-1 ring-primary/50 bg-primary/10"
                          )}
                        >
                          {selectionMode && (
                            <div className="shrink-0 mt-0.5">
                              <Checkbox
                                checked={selectedIds.has(notification.id)}
                                onCheckedChange={() => toggleSelect(notification.id)}
                              />
                            </div>
                          )}
                          <div className="min-w-0 flex-1 text-left">
                            <button
                              type="button"
                              onClick={() => handleNotificationClick(notification)}
                              className="text-sm font-medium text-foreground hover:underline"
                            >
                              {notification.title}
                            </button>
                            <p className="mt-0.5 text-xs text-muted-foreground">{notification.message}</p>
                            <p className="mt-1 text-[10px] text-muted-foreground">
                              {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                            </p>
                            {!selectionMode && !notification.is_read && (
                              <p className="mt-1 text-[10px] font-medium text-primary">Tap title to open and mark as read</p>
                            )}
                          </div>
                          {!selectionMode && (
                            <div className="flex flex-col gap-1 shrink-0">
                              {!notification.is_read && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); handleSnooze(notification); }}
                                  className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-accent"
                                  title="Snooze"
                                >
                                  <AlarmClock className="h-3.5 w-3.5" />
                                </button>
                              )}
                              {notification.is_read && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); deleteNotification(notification.id); }}
                                  className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                                  title="Delete notification"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : depositOrders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Bell className="mb-3 h-10 w-10 opacity-30" />
              <p className="text-sm">No notifications yet</p>
            </div>
          ) : null}
        </ScrollArea>

        {selectionMode && selectedIds.size > 0 && (
          <div className="sticky bottom-0 border-t border-border bg-background px-4 py-3 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={toggleSelectAll}
              className="flex items-center gap-2 text-xs text-muted-foreground"
            >
              <Checkbox
                checked={selectedIds.size === notifications.length}
                onCheckedChange={toggleSelectAll}
              />
              Select all
            </button>
            <span className="text-xs text-muted-foreground">{selectedIds.size} selected</span>
            <Button variant="destructive" size="sm" className="rounded-xl" onClick={bulkDelete}>
              <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export function useNotificationCount() {
  const { user } = useAuth();
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!user) {
      setCount(0);
      return;
    }
    const load = async () => {
      const { data, error } = await supabase
        .from("notifications")
        .select("id")
        .eq("user_id", user.id)
        .eq("is_read", false)
        .limit(500);

      if (error) {
        setCount(0);
        return;
      }
      setCount(data?.length ?? 0);
    };
    load();
    const onManualRefresh = () => { void load(); };
    window.addEventListener("notifications:changed", onManualRefresh as EventListener);
    const channel = supabase
      .channel("notif-count-rt")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` }, () => load())
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` }, () => load())
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` }, () => load())
      .subscribe();
    return () => {
      window.removeEventListener("notifications:changed", onManualRefresh as EventListener);
      supabase.removeChannel(channel);
    };
  }, [user]);

  return count;
}


export function useBankingNotificationCount() {
  const { user } = useAuth();
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      const { data: depositOrders } = await supabase
        .from("business_orders")
        .select("id, total_price, amount_paid")
        .eq("buyer_id", user.id)
        .eq("status", "deposit_paid");

      const outstanding = (depositOrders ?? []).filter((order: any) => order.total_price - order.amount_paid > 0);
      setCount(outstanding.length);
    };
    load();
    const channel = supabase
      .channel("banking-count-rt")
      .on("postgres_changes", { event: "*", schema: "public", table: "business_orders" }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user]);

  return count;
}
